<template>
  <div class="segmented-control">
    <v-radio-group
      v-model="activeOptionValue"
      class="segmented-control__container pa-1"
      :disabled="disabled"
      hide-details
      required
      inline
    >
      <AppTooltip
        v-for="option in options"
        :key="`option-${option.value}`"
        :disabled="!option.tooltip"
      >
        <template #activator="{ props: tooltipProps }">
          <div
            v-bind="tooltipProps"
            :value="option"
            class="segmented-control__item clickable"
            :class="{
              'segmented-control__item--disabled': disabled,
              'segmented-control__item--active': option.value === activeOptionValue,
              'py-2 px-4': option.name,
              'pa-1 rounded': !option.name,
            }"
            :data-locator="option.locatorValue"
            @click="activeOptionValue = option.value"
          >
            <div class="segmented-control__content">
              <app-material-icon
                v-if="option.icon"
                :name="option.icon"
                class="segmented-control__icon"
                :color="computedIconColor(option)"
                aria-hidden="true"
                :filled="option.value === activeOptionValue"
              />

              <span
                v-if="option.name"
                class="text-truncated body-md-bold"
              >
                {{ option.name }}
              </span>
            </div>
          </div>
        </template>
        <span>{{ option.tooltip }}</span>
      </AppTooltip>
    </v-radio-group>
  </div>
</template>

<script>
import { defineComponent } from 'vue'

import AppTooltip from '@/common/AppTooltip.vue'

export default defineComponent({
  name: 'AppSegmentedControl',
  components: { AppTooltip },
  props: {
    activeOption: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
    },
    /**
     * Excpected options format:
     * {
     *   name: string,
     *   value: string,
     *   locatorValue?: string,
     *   tooltip?: string,
     *   icon?: string,
     *   iconSize?: 'xxs'|'xs'|'sm'|'md'|...
     *   iconColor?: string,
     *   iconColorActive?: string,
     * }
     */
    options: {
      type: Array,
      required: true,
    },
  },
  data () {
    return {
      activeOptionValue: this.activeOption,
    }
  },
  watch: {
    activeOption (val) {
      // keep local in sync with prop
      this.activeOptionValue = val
    },
    activeOptionValue (value) {
      this.$emit('change-active-option', value)
    },
  },
  methods: {
    computedIconColor (option) {
      const isActive = option.value === this.activeOptionValue
      if (this.disabled) return 'icon-disabled'
      if (isActive) return option.iconColorActive || option.iconColor || 'icon-primary'
      return option.iconColor || 'icon-primary'
    },
  },
})
</script>

<style lang="scss">
@import "@/scss/border-tokens";

.segmented-control {
  background-color: rgb(var(--v-theme-surface-tertiary));
  @include border-radius('border-radius-3');
  align-content: center;
  display: flex;

  &__container {
    margin: 0;

    .v-input--radio-group__input {
      display: flex;
      column-gap: 8px;
      justify-content: space-between;
    }

    .v-selection-control-group--inline {
      width: 100%;
    }

    .segmented-control__item {
      align-content: center;
      @include border-radius('border-radius-3');
      color: rgb(var(--v-theme-text-tertiary));
      display: flex;
      justify-content: center;
      transition: all .12s ease-in-out;
      flex: 1;

      &--active {
        color: rgb(var(--v-theme-text-primary));
        background-color: rgb(var(--v-theme-white));
      }

      &--disabled {
        cursor: not-allowed;
        color: rgb(var(--v-theme-grey-400));
        background-color: transparent;

        &.segmented-control__item--active {
          color: rgb(var(--v-theme-grey-400));
          background-color: rgb(var(--v-theme-grey-25));
        }
      }
    }
  }

  &__content {
    display: inline-flex;
    align-items: center;
    column-gap: 8px;
    min-width: 0; /* keep truncation working */
  }

  &__icon {
    flex: 0 0 auto;
  }
}
</style>
